// owner — whose queued work is this?
//
// ── The bug this exists for ─────────────────────────────────────────────────
//
// Neither offline queue recorded who created a record. Attribution happened
// entirely on the server, from whichever bearer token happened to be attached
// when the op finally replayed:
//
//   server/routes/field.js  withClientKey(db, req.user.id, key, …)
//                           applyOp → time_entries.user_id = user.id
//
// So: a crew member clocks in with no signal, the op sits in IndexedDB, and
// they log out — or their token simply expires and App.js's `fetchUser` catch
// calls logout() for them without anyone touching the screen. The next person
// to log in on that phone triggers a replay on mount (useOffline), the POST
// carries THEIR token, and the first person's clock-in is written as a time
// entry for the second.
//
// `withClientKey` cannot catch it: it scopes idempotency keys per user
// (server/utils/clientOps.js), so under the second identity the key has never
// been seen and the op is applied as new rather than recognised as a replay.
//
// Time entries become timesheets become payroll. Both people end up wrong —
// one short the hours they worked, the other paid for a job they were never
// on — and nothing on either screen shows it happening.
//
// ── Why stamping, and not clearing on logout ────────────────────────────────
//
// Dropping the queue when somebody logs out is simpler and it is the wrong
// trade: it silently discards a clock-in the crew member watched the app
// accept. The whole point of the queue is that work survives a bad signal, and
// "survives a bad signal but not a session timeout" is not a promise anyone
// can hold in their head.
//
// Stamping keeps the work. An op belongs to the person who created it, replays
// only under that person, and waits otherwise. A phone that changes hands
// mid-shift ends up with two people's ops queued, and each replays correctly
// when its owner is signed in.
//
// ── The one-time adoption, stated plainly ───────────────────────────────────
//
// Records enqueued BEFORE this shipped carry no owner. Three options: strand
// them forever, drop them, or adopt them. Stranding and dropping both lose
// work that a crew member believes is saved, which is the failure this whole
// subsystem exists to prevent — so they are adopted by the first person to
// sign in after the upgrade.
//
// That reproduces the old behaviour exactly once, for records that predate the
// fix, and only on a phone that has been offline across the deploy. It is not
// better than the old behaviour for those records; it is the same. It is
// written down here rather than hidden because the alternative — silently
// dropping a clock-in — is worse and would look like the queue misbehaving.

// The two key names are IMPORTED, never retyped. A logout that clears
// `fw_field_now` while optimisticNow.js has moved on to another name is a
// logout that appears to work and does nothing.
import { NOW_KEY } from './optimisticNow';
import { CACHE_PREFIX } from './cache';

/**
 * May this record replay under `owner`?
 *
 * `owner` is the signed-in user's id (a number from the JWT). A record with no
 * owner is adoptable — see the note above. A record owned by somebody else
 * waits; it is never sent and never deleted.
 *
 * Both arguments are compared as STRINGS. The id arrives as a number from
 * `useAuth().user.id` and survives a round trip through JSON in IndexedDB, but
 * the localStorage fallback (storage.js, when IndexedDB is unavailable) is
 * JSON too and a future change to either could hand back `"3"` for `3`. A
 * mismatch there would silently strand somebody's whole queue, which is the
 * one failure mode worse than the bug this fixes.
 */
export function mayReplay(record, owner) {
  if (!record) return false;
  const mine = record.owner;
  if (mine === undefined || mine === null || mine === '') return true;   // pre-fix record
  if (owner === undefined || owner === null || owner === '') return false; // signed out
  return String(mine) === String(owner);
}

/** Is this a pre-fix record that the current owner should adopt? */
export function needsAdoption(record) {
  if (!record) return false;
  const mine = record.owner;
  return mine === undefined || mine === null || mine === '';
}

/**
 * The display-only state a logout must clear, by NAME.
 *
 * server/utils/__tests__/noLocalStorageClear.test.js bans
 * `localStorage.clear()` outright and says why — this app shares localStorage
 * with the auth token and the theme, and a blanket clear has taken those out
 * before. So the keys are listed rather than swept.
 *
 * Only CACHES are here. The queues are deliberately absent: they hold work,
 * the stamp above already stops it being misattributed, and clearing them on
 * logout is the data loss this module exists to avoid.
 *
 * What these two do hold is the previous person's day. `fw_field_now` is the
 * optimistic running clock, and resolveNow keeps showing it while the queue
 * still has the key — so without this the next person's Today screen shows
 * somebody else's job running. `fw_field_cache:today` is the cached day
 * bundle, shown with no signal and labelled "as of <the other person's fetch>".
 * Neither is work; both are wrong for a new sign-in.
 */
export { NOW_KEY, CACHE_PREFIX };

/** Remove the cached view of somebody else's day. Never throws. */
export function clearFieldDisplayState(store) {
  let s = store;
  if (!s) {
    try { s = typeof window !== 'undefined' ? window.localStorage : null; } catch (_) { return 0; }
  }
  if (!s) return 0;
  let removed = 0;
  try {
    s.removeItem(NOW_KEY);
    removed += 1;
    // The cache prefix is per-bundle (`today`, and any added later), so the
    // keys are discovered rather than listed — a new bundle must not survive a
    // logout just because nobody remembered to add it here.
    const doomed = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (typeof k === 'string' && k.startsWith(CACHE_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => { s.removeItem(k); removed += 1; });
  } catch (_) { /* private mode, or a store that refuses — nothing to do */ }
  return removed;
}
